import { put } from 'redux-saga/effects';
import { postWithToken } from 'api/network';
import { appNotify, setImportLoadingOverlayState } from 'store/actions';
import { parse, isBefore, startOfToday } from 'date-fns';
import { fullAddress } from 'utilities/address';
import { Constants } from 'utilities/constants';

export function* ValidateRows(importRows, importSelectedChannel, postAcqChannel, postAcqRequestType, importType, importSource) {
  try {
    yield put(setImportLoadingOverlayState({ importType, importLoadingOverlayState: { isLoading: true, message: 'Validating rows...' } }));

    const url = importType === Constants.ImportType.PROPERTIES
      ? '/api/explore/import/property/validateImportProperties'
      : importType === Constants.ImportType.OPPORTUNITIES
        ? `/api/underwrite/import/opportunity/validateImportOpportunities?channel=${importSelectedChannel}&source=${importSource}`
        : `/api/postAcquisition/import/validate`

    const payload = importType === Constants.ImportType.POSTACQTASKS
      ? { source: importSource, channel: postAcqChannel, requestType: postAcqRequestType, postAcquisitionTasks: importRows }
      : importRows

    const response = yield postWithToken(url, payload);
    const rowValidationResults = yield response.json();
    const newRows = MapValidationResultsToRows(importRows, rowValidationResults)
    yield put(setImportLoadingOverlayState({ importType, importLoadingOverlayState: { isLoading: false, message: null } }));

    return newRows;

  } catch (error) {
    yield put(setImportLoadingOverlayState({ importType, importLoadingOverlayState: { isLoading: false, message: null } }));

    const apiErrorObj = JSON.parse(error.message);  // message is a string, we need to parse it into a json object
    //console.log('apiErrorObj', apiErrorObj);
    let rowsWithInvalidColumns = [];  // this will be returned
    if (apiErrorObj.status === 400) {
      // If api returned status 400, it probably means there is data validation error (converting wrong data type), 
      // so here we parse error message and construct our own invalidColumns array
      var errorsObj = apiErrorObj.errors;
      if (errorsObj === undefined) return importRows;  // if for some reason there is no errorsObj, we just return the untouched importRows collection.
      //console.log('errorsObj', errorsObj);

      // parse backend
      var rowIndexToInvalidColumnsDictionary = {};
      for (const key in errorsObj) {
        if (errorsObj.hasOwnProperty(key)) {
          // key looks like this: '[0].beds'
          let arrayOfIndexAndFieldName = key.split('.');
          // arrayOfIndexAndFieldName looks like this ['[0]', 'beds']
          let rowIndex = Number.parseFloat(arrayOfIndexAndFieldName[0].slice(arrayOfIndexAndFieldName[0].indexOf('[') + 1, -1));  // index is the target row's index of the whole importRows collection
          let fieldName = arrayOfIndexAndFieldName[1]; // 'beds'       
          const message = errorsObj[key][0]; // error messsage(value) for this key '[0].beds' is an array, we just pick the first one

          if (rowIndex === undefined || fieldName === undefined || message === undefined) continue;

          // construct row index - invalidColumns dictionary
          if (rowIndexToInvalidColumnsDictionary.hasOwnProperty(rowIndex)) {
            rowIndexToInvalidColumnsDictionary[rowIndex].push({ name: fieldName, message: message }); // add new invalidColumn object to invalidColumn collection
          }
          else {
            let invalidColumns = [];
            invalidColumns.push({ name: fieldName, message: message });
            rowIndexToInvalidColumnsDictionary[rowIndex] = invalidColumns;
          }
        }
      }

      // deep copy importRows to rowsWithInvalidColumns, add invalidColumns if we have it in the dictionary
      for (let index = 0; index < importRows.length; index++) {
        const targetRow = importRows[index];
        if (rowIndexToInvalidColumnsDictionary.hasOwnProperty(index)) {
          rowsWithInvalidColumns.push({
            ...targetRow,
            invalidColumns: rowIndexToInvalidColumnsDictionary[index], // get invalidColumns by rowIndex from dictionary
          });
        }
        else {
          rowsWithInvalidColumns.push({
            ...targetRow
          });
        }
      }

      console.log('newRows', rowsWithInvalidColumns);
      return rowsWithInvalidColumns;
    }
    else {
      yield put(appNotify(error.message, 'error'));
    }

  }
}

export function MapValidationResultsToRows(importRows, rowValidationResults) {
  const updatedImportRows = rowValidationResults.map(rowValidationResult => {
    const targetRow = importRows.find(row => row.referenceId === rowValidationResult.referenceId);
    const invalidColumns = [];
    for (let error of rowValidationResult.validationErrors) {
      // NOTE: we are not validating lat long in this step on the server anymore, so no need for this logic
      // Server validates lat/long but we don't want to apply it to import UI, so here we leave those two out
      // But lat/long will still be validated when user submit import
      // if (error.fieldName !== 'latitude' && error.fieldName !== 'longitude') {
      //   invalidColumns.push({ name: error.fieldName, message: error.errorMessage });
      // }

      invalidColumns.push({ name: error.fieldName, message: error.errorMessage });
    }

    return {
      ...targetRow,
      invalidColumns
    }
  });

  return updatedImportRows;
}








// The code below are NOT being used, just keep them here in case we need them in the future
// 6/2: since code is not in use, not checking if updates are needed for import redux refactor
export const ValidateRow = (data, importType, row, importSelectedChannel, appSettings) => {
  let invalidColumns = [];

  Object.keys(row).forEach(col => {
    const currentValue = row[col];
    const field = appSettings.FieldSettings.find(el => el.field === col);
    if (!field) return;

    const gridRules = importType === Constants.ImportType.OPPORTUNITIES ? field.importGrid : field.importTitlesGrid;
    const required = gridRules?.requiredChannels?.includes(importSelectedChannel);
    const { displayName } = field;
    if (required) {
      const result = isNotEmpty(col, displayName, currentValue);
      if (result.status) {
        invalidColumns.push(result.message);
        return;
      }
    }
    if (gridRules?.validationRules?.length) {
      for (let i = 0; i < gridRules.validationRules.length; i++) {
        const currentRule = gridRules.validationRules[i];
        const result = functionMap[currentRule](col, displayName, currentValue, data.map(row => row[col])); // 4th argument only used by isUnique
        if (result.status) {
          invalidColumns.push(result.message);
          return;
        }
      }
    }
    if (gridRules?.acceptedValues?.length) {
      const result = isAcceptedValue(col, displayName, currentValue, gridRules.acceptedValues);
      if (result.status) {
        invalidColumns.push(result.message);
        return;
      }
    }
  });

  if (importType === Constants.ImportType.OPPORTUNITIES) { // Validate address uniqueness for opportunity import
    const compareAddresses = data.map(el => fullAddress(el));
    const result = isUnique('address', 'Address', fullAddress(row), compareAddresses);
    if (result.status) {
      ['street', 'city', 'state', 'zipCode'].forEach(field => {
        // invalidColumns.push({ name: field, message: result.message.message });
      })
      invalidColumns.push({ name: 'address', message: result.message.message });
    }
  }

  return invalidColumns;
}

const isAcceptedValue = (name, displayName, value, acceptedValues) => {
  let result = { status: false };
  if (value === null || value === undefined) {      // ok if null or empty {} 
  } else if (!acceptedValues.includes(value)) {
    result = { status: true, message: { name: name, message: displayName + ' does not have an accepted value' } };
  }
  return result;
}

const isUnique = (name, displayName, value, compareValues) => {
  let result = { status: false };
  if (value === null || value === undefined) {      // ok if null or empty {} 
  } else {
    let count = 0;
    compareValues.forEach(el => {
      if (el === value) {
        count++;
      }
    })
    if (count > 1) {
      result = { status: true, message: { name: name, message: displayName + ' must be unique' } };
    }
  }
  return result;
}

export const isNotEmpty = (name, displayName, value) => {
  let result = { status: true, message: { name: name, message: displayName + ' is required' } };
  if (value === null || value === undefined) {    // null
  } else if (typeof value === 'string' && value.replace(/\s+/g, '') === '') {  // empty {} or whitespace
  } else if (typeof value === 'object' && Object.keys(value).length === 0) {
  } else {
    result = { status: false };
  }
  return result;
};

// Validate numbers (integer and float)
export const isValidNumber = (name, displayName, value) => {
  let result = { status: false }
  if (value === null || value === undefined) {      // ok if null or empty {}
  } else if (Object.keys(value).length === 0) {
  } else if (isNaN(value)) {
    // const regex = /^(\d*\.)?\d+$/g;
    // /^\d+(?:,\d+)*(\d*\.)?\d+$/g;
    // const result = regex.test(value);
    // if (!result) {
    result = { status: true, message: { name: name, message: displayName + ' must be a valid number' } };
  }
  return result;
}
export const isPositiveNumber = (name, displayName, value) => {
  let result = isValidNumber(name, displayName, value);
  if (result.status) return result;
  if (value <= 0) {
    result = { status: true, message: { name: name, message: displayName + ' must be a positive number' } };
  }
  return result;
}

// Validate integers
export const isValidInteger = (name, displayName, value) => {
  let result = { status: false }
  if (value === null || value === undefined) {      // ok if null or empty {}
  } else if (Object.keys(value).length === 0) {
  } else if (!Number.isInteger(Number(value))) {
    result = { status: true, message: { name: name, message: displayName + ' must be a valid integer' } };
  }
  return result;
}
export const isPositiveInteger = (name, displayName, value) => {
  let result = isValidInteger(name, displayName, value);
  if (result.status) return result;
  if (value <= 0) {
    result = { status: true, message: { name: name, message: displayName + ' must be a positive integer' } };
  }
  return result;
}

const zipcodeRegex = RegExp(`^[0-9]{5}(?:-[0-9]{4})?$`); // Match format ##### or #####-####
export const isValidZipCode = (name, displayName, value) => {
  let result = { status: false }
  if (value === null || value === undefined) {
    // result = { status: true, message: { name: 'zipCode', message: 'zipcode is required' } }; // Handled by initial required check
  } else if (Object.keys(value).length === 0) {
  } else if (!zipcodeRegex.test(value)) {
    result = { status: true, message: { name: 'zipCode', message: 'Zip code must be 5 digits or ZIP+4 format' } };
  }
  return result;
}

export const isValidState = (name, displayName, value) => {
  let result = { status: false }

  if (value === null || value === undefined) {      // ok if null or empty {}
  } else if (Object.keys(value).length === 0) {
  } else if (!stateRegex.test(value)) {
    result = { status: true, message: { name: 'state', message: 'State must be a two-letter postal abbreviation' } };
  }
  return result;
}
const stateRegex = RegExp(`^(([Aa][EeLlKkSsZzRr])|([Cc][AaOoTt])|([Dd][EeCc])|([Ff][MmLl])|([Gg][AaUu])|([Hh][Ii])|([Ii][DdLlNnAa])|([Kk][SsYy])|([Ll][Aa])|([Mm][EeHhDdAaIiNnSsOoTt])|([Nn][EeVvHhJjMmYyCcDd])|([Mm][Pp])|([Oo][HhKkRr])|([Pp][WwAaRr])|([Rr][Ii])|([Ss][CcDd])|([Tt][NnXx])|([Uu][Tt])|([Vv][TtIiAa])|([Ww][AaVvIiYy]))$`);
// Match formats #/#/####, #/##/####, ##/#/####, ##/##/#### for years since 1900. Validates correct number of days for months, including leap years
const dateRegex = RegExp(/^(((0?[1-9]|1[012])\/(0?[1-9]|1\d|2[0-8])|(0?[13456789]|1[012])\/(29|30)|(0?[13578]|1[02])\/31)\/(19|[2-9]\d)\d{2}|0?2\/29\/((19|[2-9]\d)(0[48]|[2468][048]|[13579][26])|(([2468][048]|[3579][26])00)))$/);
export const isValidDate = (name, displayName, value) => {
  const checkRegex = value && dateRegex.test(value);
  let result = { status: false }
  if (value === null || value === undefined) {
    // result = { status: true, message: { name: 'auctionDate', message: 'auction date is required' } };
  } else if (Object.keys(value).length === 0) {
    // result = { status: true, message: { name: 'auctionDate', message: 'auction date is required' } };
  } else if (!checkRegex) {
    result = { status: true, message: { name: name, message: displayName + ' is not valid. Dates should be in mm/dd/yyyy format and no earlier than the year 1900' } };
  }
  return result;
}

export const isDateFuture = (name, displayName, value) => {
  let result = { status: false }
  if (isBefore(parse(value, 'MM/dd/yyyy', new Date()), startOfToday())) {
    result = { status: true, message: { name: name, message: displayName + ' cannot be in the past' } };
  }
  return result;
}

export const isBool = (name, displayName, value) => {
  let result = { status: false }
  if (value === null || value === undefined) {      // ok if null or empty {}
  } else if (typeof value !== 'boolean') {
    result = { status: true, message: { name: name, message: displayName + ' is not a boolean' } };
  }
  return result;
}

const functionMap = { // use this to call validation functions via their names as strings
  isUnique,
  isNotEmpty,
  isValidNumber,
  isPositiveNumber,
  isValidInteger,
  isPositiveInteger,
  isValidZipCode,
  isValidState,
  isValidDate,
  isDateFuture,
  isBool
}